import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, DistributiveOmit, Heading, HeadingLevel, Section, StyleProps } from "@hex-insights/core";
import {
	Form,
	FormEditButtonPair,
	FormState,
	SubmitButton,
	useInternalField,
	useInternalFields,
} from "@hex-insights/forms";
import { FieldToggle } from "../../../Components/FieldToggle";
import { PersonCreateMutation, PersonDetailQuery, PersonUpdateMutation } from "../../../Utilities";
import { PersonFormState, PersonFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<CreateProps<PersonCreateMutation["createPerson"]>, "formState" | "create"> & {
	formStateArgs?: PersonFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Person model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<PersonFormValues.Create>;
	create: CreateFunction<PersonFormValues.Create, T>;
	onSuccess: (formState: FormState<PersonFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<PersonFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Person model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "person.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<PersonFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	useInternalField(formState, "publicNameFormat");

	return (
		<React.Fragment>
			<Field.GivenName formState={formState} />
			<Field.MiddleName formState={formState} />
			<Field.FamilyName formState={formState} />
			<Field.NameOrder formState={formState} />
			<FieldToggle
				formState={formState}
				name="nickname"
				buttonVariant="tertiary"
				buttonSize="small"
				buttonChildren="Add Nickname"
				initialIsOpen={formState.formValues.nickname !== ""}
			>
				<Field.Nickname formState={formState} />
			</FieldToggle>
			<FieldToggle
				formState={formState}
				name="alternateNames"
				buttonVariant="tertiary"
				buttonSize="small"
				buttonChildren="Add Alternate Names"
				initialIsOpen={formState.formValues.alternateNames !== ""}
			>
				<Field.AlternateNames formState={formState} />
			</FieldToggle>
			<Field.Image formState={formState} />
			<Field.Gender formState={formState} />
			<Field.DateOfBirth formState={formState} />
			<Field.Nationalities.Select formState={formState} />
			<Field.Languages.Select formState={formState} />
			<Field.EnglishLanguageFluency formState={formState} />
			<Field.Occupation formState={formState} />
			<Field.NationalIdentificationNumber formState={formState} />
			<Field.GovernmentDocumentSeries formState={formState} />
			<Field.GovernmentDocumentNumber formState={formState} />
			<Field.GovernmentDocumentIssuedBy formState={formState} />
			<Field.Addresses.Select formState={formState} />
			<Field.EmailAddresses.SubForm formState={formState} />
			<Field.Employee.Select formState={formState} />
			<Field.Guardian.Select formState={formState} />
			<Field.PhoneNumbers.SubForm formState={formState} />
			<Field.Student.Select formState={formState} />
			<Field.User.SubForm formState={formState} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<DetailProps<PersonUpdateMutation["updatePerson"]>, "formState" | "update"> & {
	formStateArgs?: Partial<DistributiveOmit<PersonFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the Person model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, person: props.person });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	person: PersonDetailQuery["person"];
	formState: FormState<PersonFormValues.Detail>;
	update: UpdateFunction<PersonFormValues.Detail, T>;
	onSuccess: (formState: FormState<PersonFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<PersonFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the Person model using the given form state.
 */
export function Detail<T>({ formState, person, update, onSuccess, formNameSuffix, ...props }: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `person.detail.${person.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} person={person} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<PersonFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the Person model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, person: props.person });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	person: PersonDetailQuery["person"];
	formState: FormState<PersonFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the Person model using the given form state.
 */
export function ReadOnly({ formState, person, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} person={person} />
			</Form.Column>
		</div>
	);
}

const subTypeFields: (keyof PersonFormValues.Detail)[] = ["employeeID", "guardianID", "studentID", "userID"];

type DetailFieldsProps = {
	person: PersonDetailQuery["person"];
	formState: FormState<PersonFormValues.Detail>;
};

function DetailFields({ formState, person }: DetailFieldsProps) {
	useInternalFields(formState, subTypeFields);

	return (
		<React.Fragment>
			<Field.Name formState={formState} />
			<Field.PublicName formState={formState} />
			<Field.GivenName formState={formState} />
			<Field.MiddleName formState={formState} />
			<Field.FamilyName formState={formState} />
			<Field.NameOrder formState={formState} />
			<FieldToggle
				formState={formState}
				name="nickname"
				buttonVariant="tertiary"
				buttonSize="small"
				buttonChildren="Add Nickname"
				initialIsOpen={formState.formValues.nickname !== ""}
			>
				<Field.Nickname formState={formState} />
			</FieldToggle>
			<FieldToggle
				formState={formState}
				name="alternateNames"
				buttonVariant="tertiary"
				buttonSize="small"
				buttonChildren="Add Alternate Names"
				initialIsOpen={formState.formValues.alternateNames !== ""}
			>
				<Field.AlternateNames formState={formState} />
			</FieldToggle>
			<Field.PublicNameFormat formState={formState} />
			<Field.Image formState={formState} url={person.imageURL} />
			<Field.Gender formState={formState} />
			<Field.DateOfBirth formState={formState} />
			<Field.Nationalities.Select formState={formState} currentInstances={person.nationalities} />
			<Field.Languages.Select formState={formState} currentInstances={person.languages} />
			<Field.EnglishLanguageFluency formState={formState} />
			<Field.Occupation formState={formState} />
			<Field.NationalIdentificationNumber formState={formState} />
			<Field.GovernmentDocumentSeries formState={formState} />
			<Field.GovernmentDocumentNumber formState={formState} />
			<Field.GovernmentDocumentIssuedBy formState={formState} />
			<Field.Addresses.Select formState={formState} currentInstances={person.addresses} />
			<Field.EmailAddresses.Select formState={formState} currentInstances={person.emailAddresses} />
			<Field.PhoneNumbers.Select formState={formState} currentInstances={person.phoneNumbers} />
		</React.Fragment>
	);
}

const employeeInternalFields: (keyof PersonFormValues.CreateInEmployee)[] = [
	"guardianID",
	"studentID",
	"nationalIdentificationNumber",
	"governmentDocumentSeries",
	"governmentDocumentNumber",
	"governmentDocumentIssuedBy",
];

export type CreateFieldsInEmployeeProps = {
	formState: FormState<PersonFormValues.CreateInEmployee>;
	headingLevel?: HeadingLevel;
};

export function CreateFieldsInEmployee({ formState, headingLevel = 3 }: CreateFieldsInEmployeeProps) {
	useInternalField(formState, "publicNameFormat");
	useInternalFields(formState, employeeInternalFields);

	return (
		<React.Fragment>
			<Section>
				<Section.Body style={{ paddingTop: 0 }}>
					<Form.Column>
						<Field.GivenName formState={formState} />
						<Field.MiddleName formState={formState} />
						<Field.FamilyName formState={formState} />
						<Field.NameOrder formState={formState} />
						<FieldToggle
							formState={formState}
							name="nickname"
							buttonVariant="tertiary"
							buttonSize="small"
							buttonChildren="Add Nickname"
							initialIsOpen={formState.formValues.nickname !== ""}
						>
							<Field.Nickname formState={formState} />
						</FieldToggle>
						<FieldToggle
							formState={formState}
							name="alternateNames"
							buttonVariant="tertiary"
							buttonSize="small"
							buttonChildren="Add Alternate Names"
							initialIsOpen={formState.formValues.alternateNames !== ""}
						>
							<Field.AlternateNames formState={formState} />
						</FieldToggle>
						<Field.Image formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Demographics
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<Field.Gender formState={formState} />
						<Field.DateOfBirth formState={formState} />
						<Field.Nationalities.Select formState={formState} />
						<Field.Languages.Select formState={formState} />
						<Field.EnglishLanguageFluency formState={formState} />
						<Field.Occupation formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Contact
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column style={{ width: "100%" }}>
						<Field.Addresses.Select formState={formState} noOptional withCreate style={{ width: "100%" }} />
						<Field.EmailAddresses.SubForm formState={formState} noOptional />
						<Field.PhoneNumbers.SubForm formState={formState} noOptional />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Portal Access
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<Field.User.SubForm formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>
		</React.Fragment>
	);
}

const guardianInternalFields: (keyof PersonFormValues.CreateInGuardian)[] = ["employeeID", "studentID"];

export type CreateFieldsInGuardianProps = {
	formState: FormState<PersonFormValues.CreateInGuardian>;
	headingLevel?: HeadingLevel;
};

export function CreateFieldsInGuardian({ formState, headingLevel = 3 }: CreateFieldsInGuardianProps) {
	useInternalField(formState, "publicNameFormat");
	useInternalFields(formState, guardianInternalFields);

	return (
		<React.Fragment>
			<Section>
				<Section.Body style={{ paddingTop: 0 }}>
					<Form.Column>
						<Field.GivenName formState={formState} />
						<Field.MiddleName formState={formState} />
						<Field.FamilyName formState={formState} />
						<Field.NameOrder formState={formState} />
						<FieldToggle
							formState={formState}
							name="nickname"
							buttonVariant="tertiary"
							buttonSize="small"
							buttonChildren="Add Nickname"
							initialIsOpen={formState.formValues.nickname !== ""}
						>
							<Field.Nickname formState={formState} />
						</FieldToggle>
						<FieldToggle
							formState={formState}
							name="alternateNames"
							buttonVariant="tertiary"
							buttonSize="small"
							buttonChildren="Add Alternate Names"
							initialIsOpen={formState.formValues.alternateNames !== ""}
						>
							<Field.AlternateNames formState={formState} />
						</FieldToggle>
						<Field.Image formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Demographics
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<Field.Gender formState={formState} />
						<Field.DateOfBirth formState={formState} />
						<Field.Nationalities.Select formState={formState} />
						<Field.Languages.Select formState={formState} />
						<Field.EnglishLanguageFluency formState={formState} />
						<Field.Occupation formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section style={{ width: "100%" }}>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Contact
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<Field.Addresses.Select formState={formState} noOptional withCreate style={{ width: "100%" }} />
						<Field.EmailAddresses.SubForm formState={formState} noOptional />
						<Field.PhoneNumbers.SubForm formState={formState} noOptional />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Portal Access
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<Field.User.SubForm formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>
		</React.Fragment>
	);
}

const studentInternalFields: (keyof PersonFormValues.CreateInStudent)[] = [
	"occupation",
	"addressIDs",
	"emailAddresses",
	"employeeID",
	"guardianID",
	"phoneNumbers",
	"user",
	"governmentDocumentSeries",
	"governmentDocumentNumber",
	"governmentDocumentIssuedBy",
];

export type CreateFieldsInStudentProps = {
	formState: FormState<PersonFormValues.CreateInStudent>;
	headingLevel?: HeadingLevel;
};

export function CreateFieldsInStudent({ formState, headingLevel = 3 }: CreateFieldsInStudentProps) {
	useInternalField(formState, "publicNameFormat");
	useInternalFields(formState, studentInternalFields);

	return (
		<React.Fragment>
			<Section>
				<Section.Body style={{ paddingTop: 0 }}>
					<Form.Column>
						<Field.GivenName formState={formState} />
						<Field.MiddleName formState={formState} />
						<Field.FamilyName formState={formState} />
						<Field.NameOrder formState={formState} />
						<FieldToggle
							formState={formState}
							name="nickname"
							buttonVariant="tertiary"
							buttonSize="small"
							buttonChildren="Add Nickname"
							initialIsOpen={formState.formValues.nickname !== ""}
						>
							<Field.Nickname formState={formState} />
						</FieldToggle>
						<FieldToggle
							formState={formState}
							name="alternateNames"
							buttonVariant="tertiary"
							buttonSize="small"
							buttonChildren="Add Alternate Names"
							initialIsOpen={formState.formValues.alternateNames !== ""}
						>
							<Field.AlternateNames formState={formState} />
						</FieldToggle>
						<Field.Image formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Demographics
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<Field.Gender formState={formState} noOptional />
						<Field.DateOfBirth formState={formState} noOptional />
						<Field.Nationalities.Select formState={formState} />
						<Field.Languages.Select formState={formState} />
						<Field.EnglishLanguageFluency formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Government ID
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<Field.NationalIdentificationNumber formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>
		</React.Fragment>
	);
}
